/* ==========================================================================
   SKNBODY HOMEPAGE - BLOCK 03
   File: assets/skn-home-real-moments.js

   Responsibilities:
   - Lazy-load community videos near the viewport
   - Autoplay visible card videos muted
   - Pause videos outside the viewport
   - Open selected video in a modal
   - Modal sound control
   - Escape key and backdrop closing
   - Focus management
   - Reduced-motion support
   - Shopify Theme Editor support
   ========================================================================== */

(function () {
  'use strict';

  const ROOT_SELECTOR =
    '[data-skn-home-real-moments]';

  if (window.SKNHomeRealMoments) {
    window.SKNHomeRealMoments.initAll(document);
    return;
  }

  const sectionStates = new WeakMap();


  /* ==========================================================================
     HELPERS
     ========================================================================== */

  function getReducedMotionQuery() {
    return window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );
  }

  function getFocusableElements(container) {
    if (!container) return [];

    return Array.from(
      container.querySelectorAll(
        [
          'button:not([disabled])',
          'a[href]',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          'video[controls]',
          '[tabindex]:not([tabindex="-1"])'
        ].join(',')
      )
    ).filter(function (element) {
      return (
        !element.hasAttribute('hidden') &&
        element.getAttribute('aria-hidden') !== 'true' &&
        element.offsetParent !== null
      );
    });
  }

  function ensureVideoLoaded(video) {
    if (
      !video ||
      video.dataset.realMomentsLoaded === 'true'
    ) {
      return;
    }

    const sources = Array.from(
      video.querySelectorAll('source[data-src]')
    );

    sources.forEach(function (source) {
      const sourceUrl = source.dataset.src;

      if (!sourceUrl) return;

      source.src = sourceUrl;
      source.removeAttribute('data-src');
    });

    video.dataset.realMomentsLoaded = 'true';

    if (sources.length) {
      video.load();
    }
  }

  function playVideo(video) {
    if (!video) return;

    ensureVideoLoaded(video);

    video.muted = true;
    video.defaultMuted = true;

    const playPromise = video.play();

    if (
      playPromise &&
      typeof playPromise.catch === 'function'
    ) {
      playPromise.catch(function () {
        /*
         * Poster remains visible when autoplay
         * is declined by the browser.
         */
      });
    }
  }

  function pauseVideo(video) {
    if (!video) return;

    video.pause();
  }

  function getPreferredSource(cardVideo) {
    if (!cardVideo) return '';

    const sources = Array.from(
      cardVideo.querySelectorAll('source')
    );

    const mp4Source = sources.find(function (source) {
      const type =
        source.getAttribute('type') || '';

      return type.indexOf('video/mp4') !== -1;
    });

    const preferredSource =
      mp4Source ||
      sources[0];

    if (!preferredSource) {
      return cardVideo.currentSrc || '';
    }

    return (
      preferredSource.getAttribute('src') ||
      preferredSource.dataset.src ||
      cardVideo.currentSrc ||
      ''
    );
  }


  /* ==========================================================================
     INITIALIZE SECTION
     ========================================================================== */

  function initSection(root) {
    if (!root || sectionStates.has(root)) return;

    const cards = Array.from(
      root.querySelectorAll(
        '[data-real-moments-card]'
      )
    );

    const modal = root.querySelector(
      '[data-real-moments-modal]'
    );

    const modalVideo = root.querySelector(
      '[data-real-moments-modal-video]'
    );

    const modalReaction = root.querySelector(
      '[data-real-moments-modal-reaction]'
    );

    const modalLikes = root.querySelector(
      '[data-real-moments-modal-likes]'
    );

    const closeButtons = Array.from(
      root.querySelectorAll(
        '[data-real-moments-modal-close]'
      )
    );

    const soundButton = root.querySelector(
      '[data-real-moments-sound]'
    );

    const soundOnIcon = root.querySelector(
      '.skn-home-real-moments__sound-on'
    );

    const soundOffIcon = root.querySelector(
      '.skn-home-real-moments__sound-off'
    );

    const reducedMotionQuery =
      getReducedMotionQuery();

    const visibleCardVideos = new Set();
    const cleanupFunctions = [];

    let cardObserver = null;
    let currentCard = null;
    let previousFocusedElement = null;
    let isModalOpen = false;
    let modalMuted = false;
    let destroyed = false;


    /* ------------------------------------------------------------------------
       CARD VIDEO PLAYBACK
       ------------------------------------------------------------------------ */

    function canAutoplayCards() {
      return (
        !destroyed &&
        !isModalOpen &&
        !document.hidden &&
        !reducedMotionQuery.matches
      );
    }

    function getMaxAutoplayVideos() {
      const connection = navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;

      if (connection && connection.saveData) return 1;

      return window.matchMedia('(max-width: 767px)').matches
        ? 2
        : 3;
    }

    function playVisibleCardVideos() {
      if (!canAutoplayCards()) return;

      const maxVideos = getMaxAutoplayVideos();
      let activeCount = 0;

      visibleCardVideos.forEach(function (video) {
        if (activeCount < maxVideos) {
          playVideo(video);
          activeCount += 1;
        } else {
          pauseVideo(video);
        }
      });
    }

    function pauseAllCardVideos() {
      root
        .querySelectorAll(
          '[data-real-moments-card-video]'
        )
        .forEach(function (video) {
          pauseVideo(video);
        });
    }

    function observeCardVideos() {
      const videos = Array.from(
        root.querySelectorAll(
          '[data-real-moments-card-video]'
        )
      );

      if (!videos.length) return;

      if (!('IntersectionObserver' in window)) {
        videos.forEach(function (video) {
          visibleCardVideos.add(video);
          ensureVideoLoaded(video);
        });

        playVisibleCardVideos();
        return;
      }

      cardObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            const video = entry.target;

            if (
              entry.isIntersecting &&
              entry.intersectionRatio > 0.08
            ) {
              visibleCardVideos.add(video);
              ensureVideoLoaded(video);
            } else {
              visibleCardVideos.delete(video);
              pauseVideo(video);
            }
          });

          playVisibleCardVideos();
        },
        {
          rootMargin:
            '120px 0px 120px 0px',

          threshold: [0, 0.08, 0.3]
        }
      );

      videos.forEach(function (video) {
        cardObserver.observe(video);
      });
    }


    /* ------------------------------------------------------------------------
       MODAL SOUND STATE
       ------------------------------------------------------------------------ */

    function updateSoundButton() {
      if (!soundButton) return;

      soundButton.setAttribute(
        'aria-pressed',
        modalMuted ? 'true' : 'false'
      );

      soundButton.setAttribute(
        'aria-label',
        modalMuted
          ? 'Unmute video'
          : 'Mute video'
      );

      if (soundOnIcon) {
        soundOnIcon.hidden = modalMuted;
      }

      if (soundOffIcon) {
        soundOffIcon.hidden = !modalMuted;
      }
    }

    function toggleSound() {
      if (!modalVideo) return;

      modalMuted = !modalMuted;
      modalVideo.muted = modalMuted;

      updateSoundButton();
    }


    /* ------------------------------------------------------------------------
       OPEN MODAL
       ------------------------------------------------------------------------ */

    function openModal(card) {
      if (
        !card ||
        !modal ||
        !modalVideo
      ) {
        return;
      }

      const cardVideo = card.querySelector(
        '[data-real-moments-card-video]'
      );

      if (!cardVideo) return;

      const sourceUrl =
        getPreferredSource(cardVideo);

      if (!sourceUrl) return;

      currentCard = card;
      previousFocusedElement =
        document.activeElement;

      isModalOpen = true;
      modalMuted = false;

      pauseAllCardVideos();

      const reaction =
        card.dataset.reaction || '';

      const likes =
        card.dataset.likes || '';

      if (modalReaction) {
        modalReaction.textContent = reaction;
      }

      if (modalLikes) {
        modalLikes.textContent = likes;
      }

      modalVideo.pause();
      modalVideo.removeAttribute('src');

      while (modalVideo.firstChild) {
        modalVideo.removeChild(
          modalVideo.firstChild
        );
      }

      modalVideo.src = sourceUrl;
      modalVideo.muted = modalMuted;
      modalVideo.defaultMuted = modalMuted;

      if (cardVideo.poster) {
        modalVideo.poster = cardVideo.poster;
      } else {
        modalVideo.removeAttribute('poster');
      }

      modal.classList.add('is-open');

      modal.setAttribute(
        'aria-hidden',
        'false'
      );

      document.documentElement.classList.add(
        'skn-real-moments-modal-open'
      );

      document.body.classList.add(
        'skn-real-moments-modal-open'
      );

      updateSoundButton();

      window.setTimeout(function () {
        const playPromise =
          modalVideo.play();

        if (
          playPromise &&
          typeof playPromise.catch === 'function'
        ) {
          playPromise.catch(function () {
            /*
             * The user can start playback using
             * the native video controls.
             */
          });
        }
      }, 30);

      const focusableElements =
        getFocusableElements(modal);

      if (focusableElements.length) {
        window.setTimeout(function () {
          focusableElements[
            focusableElements.length - 1
          ].focus();
        }, 70);
      }
    }


    /* ------------------------------------------------------------------------
       CLOSE MODAL
       ------------------------------------------------------------------------ */

    function closeModal(options) {
      const settings = Object.assign(
        {
          restoreFocus: true
        },
        options || {}
      );

      if (
        !modal ||
        !isModalOpen
      ) {
        return;
      }

      isModalOpen = false;

      modal.classList.remove('is-open');

      modal.setAttribute(
        'aria-hidden',
        'true'
      );

      document.documentElement.classList.remove(
        'skn-real-moments-modal-open'
      );

      document.body.classList.remove(
        'skn-real-moments-modal-open'
      );

      if (modalVideo) {
        modalVideo.pause();
      }

      window.setTimeout(function () {
        if (!modalVideo || isModalOpen) return;

        modalVideo.removeAttribute('src');
        modalVideo.removeAttribute('poster');
        modalVideo.load();
      }, 280);

      if (
        settings.restoreFocus &&
        previousFocusedElement &&
        typeof previousFocusedElement.focus ===
          'function'
      ) {
        previousFocusedElement.focus();
      }

      previousFocusedElement = null;
      currentCard = null;

      playVisibleCardVideos();
    }


    /* ------------------------------------------------------------------------
       MODAL KEYBOARD
       ------------------------------------------------------------------------ */

    function trapModalFocus(event) {
      if (
        !isModalOpen ||
        event.key !== 'Tab' ||
        !modal
      ) {
        return;
      }

      const focusableElements =
        getFocusableElements(modal);

      if (!focusableElements.length) {
        event.preventDefault();
        return;
      }

      const firstElement =
        focusableElements[0];

      const lastElement =
        focusableElements[
          focusableElements.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    function handleDocumentKeydown(event) {
      if (!isModalOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      trapModalFocus(event);
    }


    /* ------------------------------------------------------------------------
       CARD CLICK EVENTS
       ------------------------------------------------------------------------ */

    function handleCardClick(event) {
      const card = event.currentTarget;

      if (
        card.disabled ||
        card.getAttribute('aria-disabled') ===
          'true'
      ) {
        return;
      }

      openModal(card);
    }

    cards.forEach(function (card) {
      card.addEventListener(
        'click',
        handleCardClick
      );
    });

    cleanupFunctions.push(function () {
      cards.forEach(function (card) {
        card.removeEventListener(
          'click',
          handleCardClick
        );
      });
    });


    /* ------------------------------------------------------------------------
       CLOSE AND SOUND EVENTS
       ------------------------------------------------------------------------ */

    closeButtons.forEach(function (button) {
      button.addEventListener(
        'click',
        closeModal
      );
    });

    cleanupFunctions.push(function () {
      closeButtons.forEach(function (button) {
        button.removeEventListener(
          'click',
          closeModal
        );
      });
    });

    if (soundButton) {
      soundButton.addEventListener(
        'click',
        toggleSound
      );

      cleanupFunctions.push(function () {
        soundButton.removeEventListener(
          'click',
          toggleSound
        );
      });
    }

    document.addEventListener(
      'keydown',
      handleDocumentKeydown
    );

    cleanupFunctions.push(function () {
      document.removeEventListener(
        'keydown',
        handleDocumentKeydown
      );
    });


    /* ------------------------------------------------------------------------
       PAGE VISIBILITY
       ------------------------------------------------------------------------ */

    function handleVisibilityChange() {
      if (document.hidden) {
        pauseAllCardVideos();

        if (modalVideo) {
          modalVideo.pause();
        }
      } else if (isModalOpen) {
        if (modalVideo) {
          const playPromise =
            modalVideo.play();

          if (
            playPromise &&
            typeof playPromise.catch ===
              'function'
          ) {
            playPromise.catch(function () {});
          }
        }
      } else {
        playVisibleCardVideos();
      }
    }

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    cleanupFunctions.push(function () {
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
    });


    /* ------------------------------------------------------------------------
       REDUCED MOTION
       ------------------------------------------------------------------------ */

    function handleMotionPreferenceChange() {
      if (reducedMotionQuery.matches) {
        pauseAllCardVideos();
      } else {
        playVisibleCardVideos();
      }
    }

    if (
      typeof reducedMotionQuery.addEventListener ===
      'function'
    ) {
      reducedMotionQuery.addEventListener(
        'change',
        handleMotionPreferenceChange
      );

      cleanupFunctions.push(function () {
        reducedMotionQuery.removeEventListener(
          'change',
          handleMotionPreferenceChange
        );
      });
    } else if (
      typeof reducedMotionQuery.addListener ===
      'function'
    ) {
      reducedMotionQuery.addListener(
        handleMotionPreferenceChange
      );

      cleanupFunctions.push(function () {
        reducedMotionQuery.removeListener(
          handleMotionPreferenceChange
        );
      });
    }


    /* ------------------------------------------------------------------------
       INITIAL STATE
       ------------------------------------------------------------------------ */

    observeCardVideos();
    updateSoundButton();


    /* ------------------------------------------------------------------------
       CLEANUP
       ------------------------------------------------------------------------ */

    function destroy() {
      destroyed = true;

      closeModal({
        restoreFocus: false
      });

      pauseAllCardVideos();

      if (cardObserver) {
        cardObserver.disconnect();
      }

      visibleCardVideos.clear();

      cleanupFunctions.forEach(function (
        cleanup
      ) {
        cleanup();
      });

      sectionStates.delete(root);
    }

    sectionStates.set(root, {
      destroy: destroy,
      openModal: openModal
    });
  }


  /* ==========================================================================
     INITIALIZE ALL SECTIONS
     ========================================================================== */

  function initAll(context) {
    const scope = context || document;

    if (
      scope.matches &&
      scope.matches(ROOT_SELECTOR)
    ) {
      initSection(scope);
    }

    scope
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initSection);
  }

  function destroyAll(context) {
    const scope = context || document;
    const roots = [];

    if (
      scope.matches &&
      scope.matches(ROOT_SELECTOR)
    ) {
      roots.push(scope);
    }

    scope
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(function (root) {
        roots.push(root);
      });

    roots.forEach(function (root) {
      const state =
        sectionStates.get(root);

      if (
        state &&
        typeof state.destroy === 'function'
      ) {
        state.destroy();
      }
    });
  }


  /* ==========================================================================
     PUBLIC API
     ========================================================================== */

  window.SKNHomeRealMoments = {
    initAll: initAll,
    destroyAll: destroyAll
  };


  /* ==========================================================================
     FIRST PAGE LOAD
     ========================================================================== */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        initAll(document);
      },
      {
        once: true
      }
    );
  } else {
    initAll(document);
  }


  /* ==========================================================================
     SHOPIFY THEME EDITOR
     ========================================================================== */

  document.addEventListener(
    'shopify:section:load',
    function (event) {
      initAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    function (event) {
      destroyAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:block:select',
    function (event) {
      const card = event.target.closest
        ? event.target.closest(
            '[data-real-moments-card]'
          )
        : null;

      if (!card) return;

      const root = card.closest(
        ROOT_SELECTOR
      );

      if (!root) return;

      const cardVideo = card.querySelector(
        '[data-real-moments-card-video]'
      );

      if (cardVideo) {
        ensureVideoLoaded(cardVideo);
      }

      card.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  );
})();